/**
 * useLiveTurnSignal — the open thread's spinning widget, live on every device
 * and for every participant.
 *
 * Owner report 2026-09-17, verbatim: "I asked a question on my phone and when
 * I went over to my laptop the spinning widget wasn't spinning even though
 * it's supposed to be … when somebody else or on a different device in my
 * account or another user on a different account asks a question the spinning
 * widget should show up for all … These threads really need to be live.
 * Perfectly live."
 *
 * WHAT IT REPLACES. The indicator's only cross-device source was a 45-second
 * poll (`useGlobalChat`'s ACTIVE_TASK_DISCOVERY_POLL_MS). Forty-five seconds is
 * longer than most turns take, so a laptop already sitting on the thread
 * usually showed nothing at all; and in a shared thread the question was
 * written at accept time and the answer at completion with NOTHING in between,
 * so a teammate watched silence for the whole turn. This hook listens to the
 * thread's session document instead, which the server stamps the moment a turn
 * is accepted and clears at the one terminal door every lane passes through.
 *
 * WHAT IT DOES NOT REPLACE. The poll stays exactly as it is. It remains the
 * liveness evidence a long turn needs (it re-stamps the per-thread heartbeat
 * that `planStuckProcessingClears`' ten-minute floor reads) and the fallback
 * for a participant whose session document is unreachable. This hook only
 * makes the signal instant.
 *
 * IT OWNS ONLY TURNS THIS TAB DID NOT START. `isLiveSendInFlight` already
 * tracks the task ids this tab is driving, and a turn of its own has a
 * spinner, a clock and a finalize already. Skipping those is what keeps this
 * hook from clearing a live local turn a moment before its answer paints —
 * the discipline `reconcileDiscoveryChips` has to reason about explicitly
 * (serverTaskResume.ts) and which is simply structural here.
 *
 * Scope is the OPEN thread, which is the scope Miles set: not the sidebar
 * rows, not a typing indicator, not closed threads. (The rail chip reads the
 * same per-thread flag, so a row does light up — that is inherited, not new
 * surface.)
 */
import { useEffect, useRef, useState } from 'react';
import {
  subscribeThreadTurnActivity,
  type ThreadTurnActivity,
} from '../../repositories/threadTurnActivityRepository';
import { isLiveSendInFlight } from './liveSendRegistry';

/**
 * Instant-off. Absent or anything but 'off' ⇒ on, because a signal that ships
 * dark is the lesson this repo keeps re-learning (guestChat.primaryBrain,
 * memory-retrieval). With it off the 45-second poll is the only source again —
 * today's behaviour, byte for byte.
 */
export const LIVE_TURN_SIGNAL_ENABLED =
  String(process.env.REACT_APP_LIVE_TURN_SIGNAL ?? '').trim().toLowerCase() !== 'off';

export interface LiveTurnSignalInput {
  /** The workspace the thread belongs to — the account whose session doc it is. */
  accountId: string | null | undefined;
  /** The OPEN thread. Null while nothing is open; the listener detaches. */
  threadId: string | null | undefined;
  /** The same door every other finalize path uses. */
  startProcessing: (threadId?: string | null, opts?: { turnEpochMs?: number }) => void;
  stopProcessing: (threadId?: string | null) => void;
  /** Test seam only; production uses the repository. */
  subscribe?: typeof subscribeThreadTurnActivity;
  /** Test seam only; production uses the live-send registry. */
  isOwnTurn?: (taskId: string) => boolean;
}

export interface LiveTurnSignalState {
  /**
   * Who started the turn the open thread is running, when it was not this tab.
   * Null when nothing is running, or when this tab started it. The pane
   * resolves the uid to a name and shows the NAME — no "asked", no "question"
   * (Miles, 2026-09-17: "simplify what is said with just the name").
   */
  startedByUid: string | null;
}

/**
 * Pure decision half, so the guards are testable without React or Firestore.
 * Returns what the host should do with one snapshot of the signal.
 */
export function decideLiveTurnAction(input: {
  activity: ThreadTurnActivity | null;
  /** Did this tab start the task the signal names? */
  ownTurn: boolean;
  /** Did THIS hook light this thread's flag? */
  weLitIt: boolean;
}): { action: 'light' | 'clear' | 'ignore'; startedByUid: string | null; turnEpochMs?: number } {
  const { activity, ownTurn, weLitIt } = input;
  if (activity) {
    // A turn this tab is driving already has its own indicator and its own
    // finalize. Naming its author would read as somebody else's work, and
    // adopting it would put this hook in charge of clearing a flag whose
    // owner is still writing into the pane.
    if (ownTurn) return { action: 'ignore', startedByUid: null };
    return {
      action: 'light',
      startedByUid: activity.startedByUid || null,
      ...(activity.startedAtMs > 0 ? { turnEpochMs: activity.startedAtMs } : {}),
    };
  }
  // Nothing running. Put out only a light we lit — never one the send path,
  // the discovery poll or a tool's own pending widget is responsible for.
  if (!weLitIt) return { action: 'ignore', startedByUid: null };
  return { action: 'clear', startedByUid: null };
}

export function useLiveTurnSignal(input: LiveTurnSignalInput): LiveTurnSignalState {
  const { accountId, threadId, startProcessing, stopProcessing } = input;
  const subscribe = input.subscribe ?? subscribeThreadTurnActivity;
  const isOwnTurn = input.isOwnTurn ?? isLiveSendInFlight;

  const [startedByUid, setStartedByUid] = useState<string | null>(null);
  /** Threads whose flag THIS hook lit, so it can never clear anyone else's. */
  const litRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!LIVE_TURN_SIGNAL_ENABLED) return;
    const account = (accountId || '').trim();
    const thread = (threadId || '').trim();
    if (!account || !thread) {
      setStartedByUid(null);
      return;
    }

    let detach: (() => void) | null = null;
    try {
      detach = subscribe(account, thread, (activity) => {
        const decision = decideLiveTurnAction({
          activity,
          ownTurn: Boolean(activity && isOwnTurn(activity.taskId)),
          weLitIt: litRef.current.has(thread),
        });
        if (decision.action === 'light') {
          litRef.current.add(thread);
          setStartedByUid(decision.startedByUid);
          startProcessing(thread, decision.turnEpochMs ? { turnEpochMs: decision.turnEpochMs } : undefined);
          return;
        }
        if (decision.action === 'clear') {
          litRef.current.delete(thread);
          setStartedByUid(null);
          stopProcessing(thread);
          return;
        }
        setStartedByUid(decision.startedByUid);
      });
    } catch (error) {
      // A courtesy channel never surfaces an error: the poll still carries it.
      // eslint-disable-next-line no-console
      console.warn('liveTurnSignal.subscribe unavailable — the 45s poll still carries it', error);
    }

    return () => {
      // Leaving the thread is not the turn ending. The flag is left exactly as
      // it is (the poll and the stuck-flag sweep own it from here), and only
      // the NAME is dropped, because it belongs to the view being left.
      if (detach) {
        try { detach(); } catch { /* already detached */ }
      }
      setStartedByUid(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, threadId, startProcessing, stopProcessing]);

  return { startedByUid };
}
